import { useEffect, useState } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

const MONTHS_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

function fmtDateFR(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${d} ${MONTHS_FR[m - 1]} ${y}`;
}

/** English-style grouping (comma thousands, dot decimals) — matches the original tool exactly. */
function fmtAmount(n, decimals = 2) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  const fixed = Number(n).toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
}

function fmtPct(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toFixed(2) + '%';
}

const TYPE_TITLE = { proforma: 'Facture Proforma', final: 'Facture' };

/** Same `.ids .row` k/v grid as the original template's idRow() helper. */
function IdRow({ k, v }) {
  if (!v) return null;
  return (
    <div className="row">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

/**
 * Only the emitter's (our own company's) logo ever prints on an invoice —
 * never the client's/manufacturer's. Fetched as an authenticated blob (same
 * pattern as FileViewer) so window.print() never races an unauthenticated
 * <img> request; falls back to the bundled Agevolami mark if the own
 * company has no logo uploaded.
 */
function useEmitterLogo(ownId, hasLogo) {
  const [src, setSrc] = useState('/icons/logo-agevolami.png');
  useEffect(() => {
    if (!ownId || !hasLogo) return undefined;
    let revoke;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/companies/${ownId}/logo`, { credentials: 'include' });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revoke = url;
        setSrc(url);
      } catch { /* keep the fallback logo */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [ownId, hasLogo]);
  return src;
}

/**
 * Faithful port of invoicers/index.html's printable invoice — same structure,
 * same CSS (web/src/styles/invoice.css), same "Fait à ... le ..." / bank
 * transfer block — but rendered from a saved invoice record instead of a
 * one-off local HTML form. window.print() from the parent produces the same
 * PDF clients already receive.
 */
export default function InvoicePrint({ invoice }) {
  const inv = invoice;
  const amount = Number(inv.amount || 0);
  const currency = inv.currency || 'EUR';
  const dateFR = fmtDateFR(inv.issue_date);
  const emName = inv.own_name || '—';
  const logoSrc = useEmitterLogo(inv.own_id, inv.own_has_logo);

  const footerParts = [
    emName,
    inv.own_rc ? `RC ${inv.own_rc}${inv.own_tribunal ? ` (${inv.own_tribunal})` : ''}` : null,
    inv.own_ice ? `ICE ${inv.own_ice}` : null,
    inv.own_capital ? `Capital ${inv.own_capital}` : null,
    inv.own_address ? inv.own_address.replace(/\n/g, ', ') : null,
  ].filter(Boolean).join('  ·  ');

  const contactBits = [
    inv.client_phone ? `T ${inv.client_phone}` : null,
    inv.client_email ? `M ${inv.client_email}` : null,
  ].filter(Boolean).join('  ·  ');

  return (
    <div className="invoice-print-wrap">
      <article className="invoice" aria-label="Aperçu facture">
        <div className="invoice-header">
          <div className="invoice-logo">
            <img src={logoSrc} alt={emName} />
          </div>
          <div className="invoice-title-block">
            <h1>{TYPE_TITLE[inv.type] || TYPE_TITLE.proforma}</h1>
            {inv.type === 'proforma' && <div className="doc-badge">Document non définitif</div>}
            {inv.subtitle && <p className="subtitle">{inv.subtitle}</p>}
          </div>
        </div>

        <div className="parties">
          <div className="box">
            <div className="box-title">Émetteur</div>
            <div className="box-body">
              <div className="company">{emName}</div>
              {inv.own_address && <div className="addr">{inv.own_address}</div>}
              {(inv.own_rc || inv.own_ice || inv.own_capital || inv.own_activite) && (
                <div className="ids">
                  <IdRow k="RC" v={inv.own_rc ? `${inv.own_rc}${inv.own_tribunal ? ` · ${inv.own_tribunal}` : ''}` : ''} />
                  <IdRow k="ICE" v={inv.own_ice} />
                  <IdRow k="Capital" v={inv.own_capital} />
                  <IdRow k="Activité" v={inv.own_activite} />
                </div>
              )}
              {(inv.own_rep_name || inv.own_rep_role) && (
                <div className="legal">
                  Représentant légal : {inv.own_rep_name}<br />{inv.own_rep_role}
                </div>
              )}
            </div>
          </div>
          <div className="box dest">
            <div className="box-title">Destinataire</div>
            <div className="box-body">
              <div className="company">{inv.client_name}</div>
              {inv.client_address && <div className="addr">{inv.client_address}</div>}
              {inv.client_tax_id && (
                <div className="ids"><IdRow k="P.IVA" v={inv.client_tax_id} /></div>
              )}
              {contactBits && <div className="contact-line">{contactBits}</div>}
              {inv.attn_name && (
                <div className="legal">À l'attention de<br /><strong>{inv.attn_name}</strong></div>
              )}
            </div>
          </div>
        </div>

        <div className="meta-strip">
          <div className="meta-card"><div className="k">Date</div><div className="v">{dateFR}</div></div>
          <div className="meta-card"><div className="k">N° {inv.type === 'final' ? 'facture' : 'proforma'}</div><div className="v">{inv.number}</div></div>
          <div className="meta-card"><div className="k">Devise</div><div className="v">{currency}</div></div>
        </div>

        <div className="meta-rows">
          <div className="meta-row"><div className="lbl">Référence</div><div className="val">{inv.reference}</div></div>
          <div className="meta-row"><div className="lbl">Projet</div><div className="val">{inv.project_name}</div></div>
        </div>

        <div className="section-title">Paramètres de calcul</div>
        <div className="params">
          <div className="lbl">Montant de base HT</div>
          <div className="val-inline">
            <span className="val-box">{fmtAmount(inv.base_amount)}</span>
            <span className="unit">{currency} HT</span>
          </div>
          <div className="lbl">Taux de commission</div>
          <div><span className="val-box">{fmtPct(inv.rate_pct)}</span></div>
          {inv.tranche_desc && <><div className="lbl">Tranche / description</div><div className="plain">{inv.tranche_desc}</div></>}
        </div>

        <table className="lines">
          <thead>
            <tr><th className="col-desc">Description</th><th className="col-base">Base de calcul</th><th className="col-amt">Montant ({currency})</th></tr>
          </thead>
          <tbody>
            <tr>
              <td className="col-desc">{inv.description}</td>
              <td className="col-base">{inv.base_label}</td>
              <td className="col-amt">{fmtAmount(amount)}</td>
            </tr>
          </tbody>
        </table>

        <div className="total-row">
          <div className="lbl">Total {inv.type === 'final' ? 'facture' : 'facture proforma'}</div>
          <div className="amt">{fmtAmount(amount)}</div>
        </div>

        {(inv.own_bank_name || inv.own_bank_iban) && (
          <div className="bank-block">
            <div className="section-title">Coordonnées bancaires · à confirmer avant envoi</div>
            <div className="bank-inner">
              <div className="bank">
                <div className="lbl">Banque</div><div className="val">{inv.own_bank_name}</div>
                <div className="lbl">IBAN</div><div className="val">{inv.own_bank_iban}</div>
                <div className="lbl">SWIFT/BIC</div><div className="val">{inv.own_bank_swift}</div>
                <div className="lbl">Titulaire</div><div className="val">{inv.own_bank_titulaire}</div>
              </div>
              <div className="transfer-box">
                <div className="t-lbl">Montant à virer</div>
                <div className="t-amt">{fmtAmount(amount)} {currency}</div>
              </div>
            </div>
          </div>
        )}

        <div className="sign-row">
          <div>Fait à {inv.place || 'Casablanca'}, le {dateFR}</div>
          <div className="right">
            <div>Pour {emName}</div>
            <div className="sign-space" />
            <div className="sign-label">Signature et cachet</div>
          </div>
        </div>

        <div className="invoice-footer">
          {inv.tva_note && <div className="legal-main">{inv.tva_note}</div>}
          <div className="company-strip">{footerParts}</div>
        </div>
      </article>
    </div>
  );
}
